import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { RELOCATION_FAQ_CATEGORIES } from "@/content/relocation-corpus/faq-defs";

const ADVICE_TH = [
  "รับรองเอกสารจากประเทศต้นทางให้ครบก่อนเดินทาง ทั้งทะเบียนสมรส สูติบัตร ผลการเรียน และหนังสือรับรองความประพฤติ เพราะการขอย้อนหลังหลังย้ายมาแล้วใช้เวลานานกว่าเดิมหลายเท่า",
  "ระบุในสัญญาเช่าให้ชัดว่าเจ้าบ้านหรือผู้ให้เช่ามีหน้าที่แจ้ง ตม.30 ภายใน 24 ชั่วโมง และต้องส่งใบตอบรับให้ผู้เช่าเก็บไว้ใช้ยื่นวีซ่า",
  "หากวางแผนอยู่เกิน 3 ปี ให้จดทะเบียนการเช่าที่สำนักงานที่ดินตาม ป.พ.พ. มาตรา 538 อย่าพึ่งสัญญา 1 ปีต่ออายุอัตโนมัติ",
  "ขอหนังสือรับรองถิ่นที่อยู่จากด่านตรวจคนเข้าเมืองท้องที่ไว้ล่วงหน้า เพราะเป็นเอกสารตั้งต้นของใบขับขี่ การจดทะเบียนรถ และการเปิดบัญชีธนาคารหลายแห่ง",
  "แจ้งวัตถุประสงค์การโอนเงินให้ถูกต้องตั้งแต่วันโอน หากตั้งใจซื้อห้องชุดในโควตาต่างชาติ ต้องได้เอกสารธุรกรรมเงินตราต่างประเทศที่ระบุวัตถุประสงค์ตรงกับการใช้จริง",
  "ตรวจวงเงินประกันสุขภาพขั้นต่ำของวีซ่าที่จะยื่น (Non-O-A หรือ LTR) จากประกาศฉบับล่าสุดขณะยื่น ไม่ยึดตามตัวเลขที่เคยได้ยินมา",
  "ตรวจว่าโรงเรียนอยู่ในทะเบียนของสำนักงานคณะกรรมการส่งเสริมการศึกษาเอกชน และออกผลการเรียนที่ระบบการศึกษาปลายทางยอมรับ",
  "จัดลำดับงานเป็นสาย วีซ่า → ที่พักและ ตม.30 → บัญชีธนาคารและหนังสือรับรองถิ่นที่อยู่ → ใบขับขี่และโรงเรียน การทำสลับลำดับมักทำให้ต้องยื่นซ้ำ",
];

const CAUTIONS_TH = [
  "ปล่อยให้ไม่มีการแจ้ง ตม.30 เพราะเข้าใจว่าเป็นหน้าที่ของผู้เช่า ทั้งที่มาตรา 38 กำหนดให้เป็นหน้าที่ของเจ้าบ้านหรือผู้ครอบครองเคหสถาน",
  "ยื่นรายงานตัว 90 วันช้ากว่ากำหนด ทั้งที่ยื่นได้ล่วงหน้าไม่เกิน 15 วันและหลังครบกำหนดไม่เกิน 7 วัน และการนับเริ่มใหม่ทุกครั้งที่เดินทางออกนอกประเทศ",
  "เซ็นสัญญาเช่ากับผู้ที่ไม่ใช่เจ้าของและไม่มีหนังสือมอบอำนาจ ทำให้ไม่มีสิทธิยันต่อเจ้าของตัวจริงเมื่อเกิดข้อพิพาท",
  "ไม่ทำบันทึกสภาพห้องและรายการทรัพย์สินวันเข้าอยู่ ทำให้เรียกคืนเงินประกันได้ยากเมื่อถูกอ้างว่าทรัพย์เสียหาย",
  "เข้าใจว่าทะเบียนบ้าน ท.ร.13 หรือบัตรชมพูทำให้ไม่ต้องรายงานตัว 90 วันอีก ซึ่งไม่ถูกต้อง หน้าที่นั้นผูกกับการอนุญาตให้อยู่ชั่วคราว",
  "ใช้ใบขับขี่ระหว่างประเทศต่อเนื่องยาวหลังตั้งถิ่นฐานในไทยแล้ว โดยไม่ขอใบอนุญาตขับรถของไทย ซึ่งอาจกระทบการพิจารณาของบริษัทประกันภัย",
  "ทำหนังสือมอบอำนาจให้ธนาคารด้วยถ้อยคำที่คิดขึ้นเอง ธนาคารไทยส่วนใหญ่รับเฉพาะแบบฟอร์มของธนาคารที่ลงนามต่อหน้าโนตารีและผ่านนิติกรณ์แล้ว",
  "ยื่นกรมธรรม์ประกันสุขภาพที่ไม่ระบุความคุ้มครองในประเทศไทยหรือไม่มีคำแปล ทำให้ถูกให้กลับไปแก้เอกสารและเสียคิว",
];

export const Route = createFileRoute("/relocation/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/relocation-corpus");
    return { faqs: m.RELOCATION_FAQ_TH };
  },
  component: RelocationFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ย้ายมาอยู่ไทยสำหรับชาวต่างชาติ — FAQ ${faqs.length} ข้อ`,
        description:
          "สัญญาเช่าและการจดทะเบียนการเช่า แจ้งที่พัก ตม.30 รายงานตัว 90 วัน เปิดบัญชีธนาคาร ประกันสุขภาพวีซ่า โรงเรียนบุตร ใบขับขี่ ทะเบียนบ้านเหลือง ท.ร.13 และบริการพาไปติดต่อราชการ",
        path: "/relocation/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/relocation/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ย้ายมาอยู่ไทย", url: `${site}/relocation/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/relocation/faq")) },
      ],
    };
  },
});

function RelocationFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ ย้ายมาอยู่ไทย</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ย้ายมาอยู่ไทยสำหรับชาวต่างชาติ: ที่พัก ตม.30 ธนาคาร ประกัน โรงเรียน ใบขับขี่ และทะเบียนราษฎร
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          รวม {faqs.length} คำตอบอ้างอิง พ.ร.บ.คนเข้าเมือง พ.ศ. 2522 มาตรา 37(4) และ 38, ป.พ.พ. ลักษณะเช่าทรัพย์,
          หลักเกณฑ์กรมการขนส่งทางบก, กรมการปกครอง และประกาศประกันสุขภาพของวีซ่า Non-O-A และ LTR
        </p>

        <section aria-label="หมวดคำถาม" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เลือกอ่านตามหมวด</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {RELOCATION_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/relocation/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleTh}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descTh}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_TH} locale="th" />
        <ServiceCautionBlock items={CAUTIONS_TH} locale="th" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้ทีมงานวางลำดับงานย้ายถิ่นฐานให้ก่อนเริ่ม</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งสัญชาติ ประเภทวีซ่าที่ถืออยู่ จำนวนสมาชิกครอบครัว และจังหวัดที่จะพำนัก
            ทีมงานจะแจ้งลำดับเอกสารและหน่วยงานที่ต้องยื่น สอบถามได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อทีมงาน</a>
        </section>
        <FaqSourceNote topic="relocation" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}